import { useMutation } from '@tanstack/react-query';
import {
  deleteQuoteV2ItemMutation,
  deleteQuoteV2SectionMutation
} from 'Services/Mutations/QuotesV2';
import { removeFromItems } from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

type ItemType = 'product' | 'section';

interface RemoveQuoteItemVariables {
  itemId: string;
  itemType?: ItemType;
}

/**
 * Remove any item (product or section) from quote via V2 endpoints.
 * Routes to the appropriate V2 endpoint based on itemType:
 * - product → DELETE /v2/quote/:quoteId/items/:itemId
 * - section → DELETE /v2/quote/:quoteId/sections/:sectionId
 */
export const useRemoveQuoteItem = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn: deleteItem } = deleteQuoteV2ItemMutation();
  const { mutationFn: deleteSection } = deleteQuoteV2SectionMutation();

  return useMutation({
    mutationFn: ({ itemId, itemType }: RemoveQuoteItemVariables) => {
      if (itemType === 'section') {
        return deleteSection({
          quoteId,
          sectionId: itemId,
          keepItems: false
        });
      }
      return deleteItem({ quoteId, itemId });
    },

    onMutate: async ({ itemId }) => {
      await cache.cancel();

      const previousItems = cache.getItems();

      cache.setItems(old => {
        if (!old) return old;
        return removeFromItems(old, itemId);
      });

      return { previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
